import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Bell, Smartphone, Wifi, WifiOff, Download } from 'lucide-react';
import MobileService from '../services/MobileService';
import NotificationService from '../services/NotificationService';
import OfflineService from '../services/OfflineService';
import useNotifications from '../hooks/useNotifications';
import CalendarIntegration from './CalendarIntegration';

const MobileSettings = ({ isOpen, onClose }) => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [networkStatus, setNetworkStatus] = useState({ connected: true });
  const [offlineStatus, setOfflineStatus] = useState({});
  const [sankalpReminderTime, setSankalpReminderTime] = useState('06:00');
  const [eventReminders, setEventReminders] = useState(true);
  
  const {
    isSupported: notificationsSupported,
    pendingNotifications,
    scheduleSankalpaReminder,
    sendTestNotification,
    clearAllNotifications
  } = useNotifications();

  useEffect(() => {
    const loadMobileInfo = async () => {
      // Get device info
      const info = await MobileService.getDeviceInfo();
      setDeviceInfo(info);

      // Get network status
      const network = await MobileService.getNetworkStatus();
      setNetworkStatus(network);

      // Get offline status
      const offline = OfflineService.getOfflineStatus();
      setOfflineStatus(offline);

      // Load saved preferences
      const savedTime = await MobileService.getPreference('sankalpa_reminder_time');
      if (savedTime) {
        setSankalpReminderTime(savedTime);
      }

      const savedEventReminders = await MobileService.getPreference('event_reminders_enabled');
      if (savedEventReminders !== null) {
        setEventReminders(savedEventReminders);
      }
    };

    if (isOpen) {
      loadMobileInfo();
    }

    // Listen for network changes
    const handleNetworkChange = (event) => {
      setNetworkStatus(event.detail);
    };

    window.addEventListener('network-change', handleNetworkChange);

    return () => {
      window.removeEventListener('network-change', handleNetworkChange);
    };
  }, [isOpen]);

  const handleSankalpReminderChange = async (time) => {
    setSankalpReminderTime(time);
    await MobileService.setPreference('sankalpa_reminder_time', time);
    
    if (notificationsSupported) {
      await scheduleSankalpaReminder(time);
    }
  };

  const handleEventRemindersToggle = async (enabled) => {
    setEventReminders(enabled);
    await MobileService.setPreference('event_reminders_enabled', enabled);
  };

  const handleTestNotification = async () => {
    const success = await sendTestNotification();
    if (success) {
      alert('Test notification scheduled! Check your notifications in a few seconds.');
    } else {
      alert('Failed to schedule test notification. Please check permissions.');
    }
  };

  const handleClearOfflineData = async () => {
    if (confirm('Are you sure you want to clear all offline data? This cannot be undone.')) {
      await OfflineService.clearOfflineData();
      setOfflineStatus(OfflineService.getOfflineStatus());
      alert('Offline data cleared successfully.');
    }
  };

  const handleVibrate = () => {
    MobileService.vibrate([100, 50, 100]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto mobile-modal">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Smartphone className="h-6 w-6" />
              Mobile Settings
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl font-semibold"
            >
              ×
            </button>
          </div>

          {/* Device Information */}
          {deviceInfo && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-sm">Device Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Platform:</span>
                    <span className="font-medium">{deviceInfo.platform}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Model:</span>
                    <span className="font-medium">{deviceInfo.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>OS Version:</span>
                    <span className="font-medium">{deviceInfo.operatingSystem} {deviceInfo.osVersion}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>App Version:</span>
                    <span className="font-medium">1.0.0</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Network Status */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                {networkStatus.connected ? (
                  <Wifi className="h-4 w-4 text-green-600" />
                ) : (
                  <WifiOff className="h-4 w-4 text-red-600" />
                )}
                Network Status
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={`font-medium ${networkStatus.connected ? 'text-green-600' : 'text-red-600'}`}>
                    {networkStatus.connected ? 'Online' : 'Offline'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Connection:</span>
                  <span className="font-medium">{networkStatus.connectionType || 'Unknown'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          {notificationsSupported && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {/* Daily Sankalpa Reminder */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Daily Sankalpa Reminder</label>
                  <input
                    type="time"
                    value={sankalpReminderTime}
                    onChange={(e) => handleSankalpReminderChange(e.target.value)}
                    className="w-full mobile-input"
                  />
                  <p className="text-xs text-gray-600">
                    Daily reminder for Sankalpa meditation
                  </p>
                </div>

                {/* Event Reminders */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Event Reminders</label>
                    <p className="text-xs text-gray-600">
                      Get notified about upcoming events
                    </p>
                  </div>
                  <Switch
                    checked={eventReminders}
                    onCheckedChange={handleEventRemindersToggle}
                  />
                </div>

                {/* Pending Notifications */}
                {pendingNotifications.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Pending Notifications ({pendingNotifications.length})
                    </label>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {pendingNotifications.slice(0, 3).map((notification, index) => (
                        <div key={index} className="text-xs bg-gray-50 p-2 rounded">
                          {notification.title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notification Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleTestNotification}
                    className="flex-1 mobile-button"
                    variant="outline"
                    size="sm"
                  >
                    Test Notification
                  </Button>
                  <Button
                    onClick={clearAllNotifications}
                    className="flex-1 mobile-button"
                    variant="outline" 
                    size="sm"
                  >
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Calendar Integration */}
          <CalendarIntegration />

          {/* Offline Data */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Download className="h-4 w-4" />
                Offline Data
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={`font-medium ${offlineStatus.isOnline ? 'text-green-600' : 'text-amber-600'}`}>
                    {offlineStatus.isOnline ? 'Online' : 'Offline Mode'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Cached Data:</span>
                  <span className="font-medium">{offlineStatus.cachedDataKeys?.length || 0} items</span>
                </div>
                {offlineStatus.hasOfflineEvents && (
                  <div className="flex justify-between">
                    <span>Offline Events:</span>
                    <span className="font-medium text-amber-600">Pending Sync</span>
                  </div>
                )}
              </div>
              
              <Button
                onClick={handleClearOfflineData}
                className="w-full mobile-button"
                variant="outline"
                size="sm"
              >
                Clear Offline Data
              </Button>
            </CardContent>
          </Card>

          {/* Mobile Features */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-sm">Mobile Features</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                onClick={handleVibrate}
                className="w-full mobile-button"
                variant="outline"
                size="sm"
              >
                Test Vibration
              </Button>
            </CardContent>
          </Card>

          {/* Close Button */}
          <Button
            onClick={onClose}
            className="w-full mobile-button"
          >
            Close Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MobileSettings;